import { getCourseFileObjectKey } from "./signedCourseFileUrl";
import { getNodeAtPath } from "./temarioTree";

/**
 * @param {Record<string, unknown> | null | undefined} archivo
 * @param {string} requestedKey
 * @returns {boolean}
 */
function matchesRequestedFile(archivo, requestedKey) {
  if (!archivo || typeof archivo !== "object") {
    return false;
  }
  const objectKey = getCourseFileObjectKey(archivo);
  return objectKey === requestedKey || archivo.nombre === requestedKey;
}

/**
 * @param {Record<string, unknown> | null | undefined} node
 * @param {number[]} path
 * @param {string} requestedKey
 * @returns {{
 *   path: number[],
 *   archivoIndex: number,
 *   archivo: Record<string, unknown>,
 *   node: Record<string, unknown>,
 * } | null}
 */
function findInNode(node, path, requestedKey) {
  if (!node || typeof node !== "object") {
    return null;
  }
  const archivos = Array.isArray(node.archivos) ? node.archivos : [];
  for (let archivoIndex = 0; archivoIndex < archivos.length; archivoIndex += 1) {
    const archivo = archivos[archivoIndex];
    if (matchesRequestedFile(archivo, requestedKey)) {
      return { path, archivoIndex, archivo, node };
    }
  }
  const hijos = Array.isArray(node.hijos) ? node.hijos : [];
  for (let childIndex = 0; childIndex < hijos.length; childIndex += 1) {
    const found = findInNode(hijos[childIndex], [...path, childIndex], requestedKey);
    if (found) {
      return found;
    }
  }
  return null;
}

/**
 * Locates a file by storage key or name. The index is the file's position in
 * that node's own archivos list, not a flattened descendant list.
 * A preferred path is searched first so a deep link does not match an earlier copy.
 *
 * @param {Array<Record<string, unknown>> | null | undefined} nodes
 * @param {string} requestedKey
 * @param {number[]} [preferredPath]
 * @returns {ReturnType<typeof findInNode>}
 */
export function findCourseFileLocation(nodes, requestedKey, preferredPath = []) {
  const key = typeof requestedKey === "string" ? requestedKey.trim() : "";
  if (!key || !Array.isArray(nodes)) {
    return null;
  }

  if (Array.isArray(preferredPath) && preferredPath.length > 0) {
    const preferredNode = getNodeAtPath(nodes, preferredPath);
    if (preferredNode) {
      const inPreferred = findInNode(preferredNode, [...preferredPath], key);
      if (inPreferred) {
        return inPreferred;
      }
    }
  }

  for (let index = 0; index < nodes.length; index += 1) {
    const found = findInNode(nodes[index], [index], key);
    if (found) {
      return found;
    }
  }
  return null;
}

/**
 * Searches displayed tab trees in order. Campus home has no selected tab, so a
 * file that lives only in a later tab is still found.
 *
 * @param {Array<{ tab?: string, nodes?: Array<Record<string, unknown>> }> | null | undefined} tabs
 * @param {string} requestedKey
 * @param {number[]} [preferredPath]
 * @returns {{ tab: string, location: { path: number[], archivoIndex: number, archivo: Record<string, unknown>, node: Record<string, unknown> } } | null}
 */
export function findCourseFileInTabs(tabs, requestedKey, preferredPath = []) {
  if (!Array.isArray(tabs)) {
    return null;
  }
  for (const entry of tabs) {
    const location = findCourseFileLocation(entry?.nodes, requestedKey, preferredPath);
    if (!location) {
      continue;
    }
    return {
      tab: typeof entry?.tab === "string" ? entry.tab : "",
      location,
    };
  }
  return null;
}

/**
 * @param {number[] | null | undefined} prefix
 * @param {number[] | null | undefined} path
 * @returns {boolean}
 */
function isPathPrefix(prefix, path) {
  if (!Array.isArray(prefix) || !Array.isArray(path) || prefix.length === 0 || prefix.length > path.length) {
    return false;
  }
  return prefix.every((value, index) => value === path[index]);
}

/**
 * @param {number[] | null | undefined} left
 * @param {number[] | null | undefined} right
 * @returns {boolean}
 */
function samePath(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}

/**
 * Picks the accordion row for a resolved file. Synthesized file rows match the
 * exact path and local index. Nested files use the deepest node row that
 * contains that path, so an earlier duplicate does not steal the section.
 *
 * @param {Array<Record<string, unknown>> | null | undefined} rows
 * @param {{ path?: number[], archivoIndex?: number } | null | undefined} location
 * @returns {Record<string, unknown> | null}
 */
export function selectAccordionRowForFileLocation(rows, location) {
  if (!location || !Array.isArray(location.path) || !Array.isArray(rows)) {
    return null;
  }
  let best = null;
  let bestLength = -1;
  for (const row of rows) {
    const navPath = Array.isArray(row?.navPath) ? row.navPath : [];
    const matches = row?.kind === "archivo"
      ? samePath(navPath, location.path) && row.archivoIndex === location.archivoIndex
      : row?.kind === "node" && isPathPrefix(navPath, location.path);
    if (!matches || navPath.length <= bestLength) {
      continue;
    }
    best = row;
    bestLength = navPath.length;
  }
  return best;
}
